import { toDateString } from '../../utils/dateHelper';

import {
  type LimitSetupDraft,
  percentExpressionField,
} from './createInvestmentLimitModel';
import {
  type AggregationMethod,
  aggregationMethodSchema,
  filterOnlyLimitModeSchema,
  type LimitMode,
  limitModeSchema,
  type LimitType,
  limitTypeSchema,
  operatorSchema,
  type OperatorValue,
  type PortfolioFilterCondition,
  portfolioFilterConditionSchema,
  type ValueType,
  valueTypeSchema,
} from './investmentLimitValidation';

export function limitRecordToDraft(
  row: Readonly<Record<string, unknown>>
): LimitSetupDraft {
  const limitMode = parseLimitMode(row.limit_type);
  const valueType = parseValueType(row.comparison_type);
  const filterOnly = filterOnlyLimitModeSchema.safeParse(limitMode).success;
  const isRelative = !filterOnly && valueType === 'relative';
  const percentField = percentExpressionField(limitMode, valueType);
  const parameterFilters = portfolioFiltersFromStored(row.portfolio_filters);
  const denominatorFilters = portfolioFiltersFromStored(
    row.ref_portfolio_filters
  );

  return {
    breachWorkflowApproval: parseStoredBoolean(row.breach_approval_required),
    description: parseStoredText(row.description),
    limitMode,
    limitName: parseStoredText(row.limit_name),
    limitType: parseLimitType(row.limit_category),
    parameter: {
      aggregationMethod: parseAggregationMethod(row.aggregation_method),
      aggregationNumber: parseStoredNumberText(row.aggregation_number),
      currencyType: parseNullableText(row.currency_type),
      dataField: filterOnly ? null : parseNullableText(row.kpi),
      dataLevelId: parseNullableText(row.data_level),
      denominator: isRelative
        ? {
            currencyType: parseNullableText(row.ref_currency_type),
            dataLevelId: parseNullableText(row.ref_data_level),
            enabledFilterKeys: denominatorFilters.enabledFilterKeys,
            filterValues: denominatorFilters.filterValues,
          }
        : null,
      enabledFilterKeys: parameterFilters.enabledFilterKeys,
      evalMetric: parseNullableText(row.eval_metric),
      evalOperator: parseOperator(row.eval_operator),
      evalValue: parseStoredExpressionText(
        row.eval_value,
        percentField === 'evalValue'
      ),
      filterValues: parameterFilters.filterValues,
      limitValue: parseStoredExpressionText(
        row.limit_value,
        percentField === 'limitValue'
      ),
      ruleOperator: parseOperator(row.limit_operator),
      valueType,
      warningThreshold: ratioToPercentText(row.limit_value_warn),
    },
    validFrom: parseStoredDate(row.start_date),
    validIndefinitely: isStoredDateEmpty(row.end_date),
    validTo: parseStoredDate(row.end_date),
  };
}

function parseLimitMode(value: unknown): LimitMode {
  const parsed = limitModeSchema.safeParse(value);
  return parsed.success ? parsed.data : 'Aggregation';
}

function parseLimitType(value: unknown): LimitType | null {
  const parsed = limitTypeSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

function parseValueType(value: unknown): ValueType {
  const parsed = valueTypeSchema.safeParse(value);
  return parsed.success ? parsed.data : 'absolute';
}

function parseAggregationMethod(value: unknown): AggregationMethod | null {
  const parsed = aggregationMethodSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

function parseOperator(value: unknown): OperatorValue | null {
  const parsed = operatorSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

function parseStoredBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1';
  }
  return false;
}

function parseStoredText(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  return '';
}

function parseNullableText(value: unknown): string | null {
  if (value == null) {
    return null;
  }
  const text = parseStoredText(value).trim();
  return text.length === 0 ? null : text;
}

function parseStoredNumberText(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed !== '' && Number.isFinite(Number(trimmed))) {
      return trimmed;
    }
  }
  return '';
}

function parseStoredExpressionText(value: unknown, asPercent: boolean) {
  return asPercent ? ratioToPercentText(value) : parseStoredNumberText(value);
}

function ratioToPercentText(value: unknown): string {
  const ratio =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number(value.trim())
        : Number.NaN;
  if (!Number.isFinite(ratio)) {
    return '';
  }

  const percent = Math.round(ratio * 100 * 1e8) / 1e8;
  return String(percent);
}

function isStoredDateEmpty(value: unknown): boolean {
  return value == null || (typeof value === 'string' && value.trim() === '');
}

function parseStoredDate(value: unknown): Date | null {
  if (isStoredDateEmpty(value)) {
    return null;
  }

  const text =
    value instanceof Date && !Number.isNaN(value.getTime())
      ? toDateString(value)
      : parseStoredText(value);
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  if (match == null) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
}

function portfolioFiltersFromStored(value: unknown): {
  enabledFilterKeys: string[];
  filterValues: Record<string, string[]>;
} {
  const conditions = parseStoredPortfolioFilters(value);
  const enabledFilterKeys: string[] = [];
  const filterValues: Record<string, string[]> = {};

  for (const condition of conditions) {
    const values =
      condition.op === 'eq' || condition.op === 'ne'
        ? [condition.value]
        : condition.value;
    if (!enabledFilterKeys.includes(condition.field)) {
      enabledFilterKeys.push(condition.field);
    }
    filterValues[condition.field] = [
      ...(filterValues[condition.field] ?? []),
      ...values,
    ];
  }

  return { enabledFilterKeys, filterValues };
}

function parseStoredPortfolioFilters(
  value: unknown
): PortfolioFilterCondition[] {
  if (value == null || (typeof value === 'string' && value.trim() === '')) {
    return [];
  }

  let parsedValue: unknown = value;
  if (typeof value === 'string') {
    try {
      parsedValue = JSON.parse(value);
    } catch {
      return [];
    }
  }

  if (!Array.isArray(parsedValue)) {
    return [];
  }

  return parsedValue.flatMap((item) => {
    const parsed = portfolioFilterConditionSchema.safeParse(item);
    return parsed.success ? [parsed.data] : [];
  });
}
